/**
 * Leads created as Won / Won Recurring (directly won)
 * → hide from Quoted P&L; show on Actual P&L only after manager submit.
 */

const normalizeStatus = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const isWonLeadStatusValue = (status) => {
  const normalized = normalizeStatus(status);
  return normalized === "won" || normalized === "won recurring";
};

const isTruthyFlag = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value).toLowerCase() === "true";

export const getLeadQuotedExpenseNum = (lead) => {
  if (!lead || typeof lead !== "object") return 0;
  const raw =
    lead.quotedExpense ??
    lead.QuotedExpense ??
    lead.quoted_expense ??
    lead.quotedExpenses ??
    lead.QuotedExpenses;
  if (raw === undefined || raw === null || raw === "") return 0;
  const n = parseFloat(String(raw).replace(/,/g, "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
};

const collectLeadProjects = (lead, projects = []) => {
  if (Array.isArray(lead?.projects)) return lead.projects;

  const qid =
    lead?.quotationId != null && String(lead.quotationId).trim() !== ""
      ? String(lead.quotationId).trim()
      : "";
  if (!qid || !Array.isArray(projects) || projects.length === 0) return [];

  return projects.filter(
    (proj) =>
      proj?.quotationId != null && String(proj.quotationId).trim() === qid
  );
};

/**
 * Explicit create-as-Won marker only (flags / comment tag).
 * Use this for bucket routing so RR→Accept→Won leads are never treated as direct-won.
 */
export function isExplicitDirectWonLead(lead) {
  if (!lead || typeof lead !== "object") return false;

  if (
    isTruthyFlag(lead.isDirectWon) ||
    isTruthyFlag(lead.is_direct_won) ||
    isTruthyFlag(lead.directWon)
  ) {
    return true;
  }

  const comments = String(lead.comments ?? lead.Comments ?? "");
  return comments.includes("[DIRECT_WON]");
}

/**
 * @param {object} lead
 * @param {Array} [projects] optional manager projects
 * @param {{ quotedExpenseNum?: number }} [options] optional displayed Quoted Expenses (P&L column)
 */
export function isDirectWonLead(lead, projects = [], options = {}) {
  if (!lead || typeof lead !== "object") return false;

  const leadProjects = collectLeadProjects(lead, projects);

  if (isExplicitDirectWonLead(lead)) {
    if (!isWonLeadStatusValue(lead.Status)) return false;

    // Pure create-as-Won keeps SalesPersonStatus as "project requested" and must stay off Quoted.
    // Re-routed path (Won → Pending without forward → Project Requests → Response Received → Won)
    // sets SalesPersonStatus to project approved / later buckets via EditLead — those belong on Quoted.
    const spStatus = normalizeStatus(
      lead.SalesPersonStatus ?? lead.salesPersonStatus
    );
    const reenteredQuotedFlow = new Set([
      "project approved",
      "project assigned",
      "project ongoing",
      "project complete",
      "response received",
    ]);
    if (reenteredQuotedFlow.has(spStatus)) return false;

    return true;
  }

  if (!isWonLeadStatusValue(lead.Status)) return false;

  // Project Approved: Won + no prior ops project rows
  if (leadProjects.length === 0) return true;

  const expenseFromOption =
    options.quotedExpenseNum != null && Number.isFinite(Number(options.quotedExpenseNum))
      ? Number(options.quotedExpenseNum)
      : null;
  const expenseNum =
    expenseFromOption != null ? expenseFromOption : getLeadQuotedExpenseNum(lead);

  // Directly won / no quoted-expense basket: Won with zero Quoted Expenses → Actual only.
  // (Do not require "no accept" — RR Accept with 0 cost still belongs on Actual for these.)
  return expenseNum <= 0;
}

/** Prefix comments so isDirectWon survives backends that drop unknown fields. */
export function withDirectWonCommentMarker(comments = "") {
  const text = String(comments || "");
  if (text.includes("[DIRECT_WON]")) return text;
  return text ? `[DIRECT_WON] ${text}` : "[DIRECT_WON]";
}
